import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sparkles, Eye, EyeOff, ArrowLeft, Mail, Key, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, rememberMe);
      toast.success('Welcome back to the cosmos!', {
        duration: 5000,
        closeButton: true,
      });
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center p-6 relative z-10">
      {/* T3B3TU Credit */}
      <div className="fixed top-3 left-3 z-50 text-[10px] text-cosmic-gold/70 font-mono tracking-wider">
        T3B3TU
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-cosmic-gold transition-colors mb-8">
          <ArrowLeft size={20} />
          <span>Back to CosmiCards</span>
        </Link>

        <div className="glass-card p-8">
          <div className="text-center mb-8">
            <Sparkles className="w-12 h-12 text-cosmic-gold mx-auto mb-4" />
            <h1 className="font-heading text-3xl gold-text mb-2">Welcome Back</h1>
            <p className="text-slate-400">Enter your credentials to access the cosmic realm</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="starseer@cosmos.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-cosmic-gold focus:ring-cosmic-gold focus:ring-offset-0"
              />
              <span className="text-sm text-slate-400">Remember me</span>
            </label>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary h-12 text-base"
              data-testid="login-submit-btn"
            >
              {loading ? 'Entering...' : 'Enter the Cosmos'}
            </Button>
          </form>

          <div className="text-center mt-4">
            <Link to="/forgot-password" className="text-sm text-slate-400 hover:text-cosmic-gold transition-colors">
              Forgot your password?
            </Link>
          </div>

          <p className="text-center mt-4 text-slate-400">
            New to CosmiCards?{' '}
            <Link to="/register" className="text-cosmic-gold hover:underline" data-testid="register-link">
              Create Account
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState('');
  const [referrerInfo, setReferrerInfo] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Capture ?ref=USERNAME on mount and persist for resilience
  useEffect(() => {
    const fromUrl = searchParams.get('ref');
    if (fromUrl) {
      const clean = fromUrl.trim().toLowerCase().slice(0, 30);
      if (clean) {
        setReferralCode(clean);
        try { localStorage.setItem('cosmicards_ref', clean); } catch {}
      }
    } else {
      try {
        const stored = localStorage.getItem('cosmicards_ref');
        if (stored) setReferralCode(stored);
      } catch {}
    }
  }, [searchParams]);

  // Lookup recruiter info for friendly preview
  useEffect(() => {
    if (!referralCode) { setReferrerInfo(null); return; }
    let cancelled = false;
    axios.get(`${API}/referral/lookup/${encodeURIComponent(referralCode)}`)
      .then(({ data }) => { if (!cancelled && data?.valid) setReferrerInfo(data.recruiter); })
      .catch(() => { if (!cancelled) setReferrerInfo(null); });
    return () => { cancelled = true; };
  }, [referralCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await register(email, password, name, referralCode || undefined);
      try { localStorage.removeItem('cosmicards_ref'); } catch {}
      if (result.verificationCode) {
        setVerificationCode(result.verificationCode);
        setRegistrationComplete(true);
        toast.success('Account created! Please save your verification code.');
      } else {
        toast.success('Welcome to the cosmic journey!');
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Show verification code screen after registration
  if (registrationComplete) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center p-6 relative z-10">
        <div className="fixed top-3 left-3 z-50 text-[10px] text-cosmic-gold/70 font-mono tracking-wider">
          T3B3TU
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <div className="glass-card p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="font-heading text-3xl gold-text mb-2">Welcome, {name}!</h1>
            <p className="text-slate-400 mb-6">Your account has been created successfully.</p>
            
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
              <p className="text-xs text-amber-200/70 mb-2">Your email verification code:</p>
              <p className="font-mono text-3xl text-amber-400 font-bold tracking-widest">{verificationCode}</p>
              <p className="text-xs text-slate-500 mt-3">
                Save this code! You can verify your email anytime from the banner at the top of the page.
              </p>
            </div>

            <Button
              onClick={() => navigate('/')}
              className="w-full btn-primary h-12 text-base"
            >
              Start Exploring ✨
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center p-6 relative z-10">
      {/* T3B3TU Credit */}
      <div className="fixed top-3 left-3 z-50 text-[10px] text-cosmic-gold/70 font-mono tracking-wider">
        T3B3TU
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-cosmic-gold transition-colors mb-8">
          <ArrowLeft size={20} />
          <span>Back to CosmiCards</span>
        </Link>

        <div className="glass-card p-8">
          <div className="text-center mb-8">
            <Sparkles className="w-12 h-12 text-cosmic-gold mx-auto mb-4" />
            <h1 className="font-heading text-3xl gold-text mb-2">Begin Your Journey</h1>
            <p className="text-slate-400">Create an account to unlock your cosmic potential</p>
          </div>

          {referrerInfo && (
            <div
              data-testid="register-referrer-banner"
              className="mb-5 p-3 rounded-lg bg-cosmic-gold/10 border border-cosmic-gold/30 text-center text-sm text-cosmic-gold"
            >
              You were invited by <span className="font-semibold">@{referrerInfo.username}</span> 🌟
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Star Seeker"
                required
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="starseer@cosmos.com"
                required
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary h-12 text-base"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating...' : 'Start Your Journey'}
            </Button>
          </form>

          <p className="text-center mt-6 text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="text-cosmic-gold hover:underline" data-testid="login-link">
              Sign In
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('request'); // 'request', 'reset', 'success'
  const [generatedToken, setGeneratedToken] = useState('');
  const navigate = useNavigate();

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      if (response.data.reset_token) {
        setGeneratedToken(response.data.reset_token);
        toast.success('Reset token generated!');
      }
      setStep('reset');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      toast.success('Password reset successfully!');
      setStep('success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center p-6 relative z-10">
      {/* T3B3TU Credit */}
      <div className="fixed top-3 left-3 z-50 text-[10px] text-cosmic-gold/70 font-mono tracking-wider">
        T3B3TU
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Link to="/login" className="inline-flex items-center gap-2 text-slate-400 hover:text-cosmic-gold transition-colors mb-8">
          <ArrowLeft size={20} />
          <span>Back to Login</span>
        </Link>

        <div className="glass-card p-8">
          {step === 'request' && (
            <>
              <div className="text-center mb-8">
                <Mail className="w-12 h-12 text-cosmic-gold mx-auto mb-4" />
                <h1 className="font-heading text-3xl gold-text mb-2">Forgot Password</h1>
                <p className="text-slate-400">Enter your email to receive a reset token</p>
              </div>

              <form onSubmit={handleRequestReset} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary h-12 text-base"
                >
                  {loading ? 'Processing...' : 'Get Reset Token'}
                </Button>
              </form>
            </>
          )}

          {step === 'reset' && (
            <>
              <div className="text-center mb-8">
                <Key className="w-12 h-12 text-cosmic-gold mx-auto mb-4" />
                <h1 className="font-heading text-3xl gold-text mb-2">Reset Password</h1>
                <p className="text-slate-400">Enter the token and your new password</p>
              </div>

              {generatedToken && (
                <div className="mb-6 p-4 bg-cosmic-gold/10 border border-cosmic-gold/30 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">Your reset token:</p>
                  <p className="font-mono text-lg text-cosmic-gold font-bold tracking-wider">{generatedToken}</p>
                  <p className="text-xs text-slate-500 mt-2">Copy this token and enter it below. Expires in 1 hour.</p>
                </div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="token">Reset Token</Label>
                  <Input
                    id="token"
                    type="text"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value.toUpperCase())}
                    placeholder="XXXXXXXX"
                    className="font-mono tracking-wider uppercase"
                    maxLength={8}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">Minimum 6 characters</p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary h-12 text-base"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>

              <button
                onClick={() => setStep('request')}
                className="w-full mt-4 text-sm text-slate-400 hover:text-cosmic-gold transition-colors"
              >
                ← Request a new token
              </button>
            </>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="font-heading text-3xl gold-text mb-2">Password Reset!</h1>
              <p className="text-slate-400 mb-6">Your password has been successfully reset.</p>
              <Button
                onClick={() => navigate('/login')}
                className="btn-primary h-12 px-8 text-base"
              >
                Go to Login
              </Button>
            </div>
          )}

          {step !== 'success' && (
            <p className="text-center mt-6 text-slate-400">
              Remember your password?{' '}
              <Link to="/login" className="text-cosmic-gold hover:underline">
                Sign In
              </Link>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
};
